import { cs } from '../../lib/currency';
import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import api from '../../services/api';
import { toast } from 'sonner';
import { useBusinessTimezone, getTodayInTz } from '../../utils/businessTime';
import { 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign,
  UserCheck,
  UserPlus,
  CheckCircle,
  AlertCircle,
  Shield,
  Clock,
  ChevronRight,
  ArrowLeft,
  MessageSquare,
  MessageCircle,
  Filter,
  Archive,
  CheckCircle2,
  ExternalLink,
  ClipboardList,
  Loader2
} from 'lucide-react';
import TeamChat from '../../components/chat/TeamChat';

// Clickable Location Component - opens Google Maps
const LocationLink = ({ location, locationUrl, className = "" }) => {
  if (!location) return null;
  
  // Use provided URL if available, otherwise generate search URL
  let mapsUrl;
  if (locationUrl && (locationUrl.startsWith('http://') || locationUrl.startsWith('https://'))) {
    mapsUrl = locationUrl;
  } else {
    // Fallback: Clean and format the location for Google Maps search
    const cleanLocation = location.trim().replace(/\s+/g, ' ');
    mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(cleanLocation)}`;
  }
  
  return (
    <a 
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer ${className}`}
      onClick={(e) => e.stopPropagation()}
      title="Open in Google Maps"
    >
      <MapPin className="h-3 w-3" />
      <span>{location}</span>
      <ExternalLink className="h-3 w-3 opacity-50" />
    </a>
  );
};

const statusColors = {
  future: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  closed: 'bg-yellow-100 text-yellow-700',
  past: 'bg-gray-100 text-gray-700',
};

const registrationStatusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  selected: 'bg-blue-100 text-blue-700',
  payment_pending: 'bg-orange-100 text-orange-700',
  confirmed: 'bg-green-100 text-green-700',
  not_selected: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

const rsvpColors = {
  yes: 'bg-green-100 text-green-700',
  no: 'bg-red-100 text-red-700',
  tentative: 'bg-yellow-100 text-yellow-700',
};

const CoachTournaments = () => {
  const businessTz = useBusinessTimezone();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  
  // Expanded tournament cards (collapsed by default)
  const [expandedTournamentCards, setExpandedTournamentCards] = useState(new Set());
  
  const toggleTournamentCard = (tournamentId) => {
    setExpandedTournamentCards(prev => {
      const next = new Set(prev);
      if (next.has(tournamentId)) {
        next.delete(tournamentId);
      } else {
        next.add(tournamentId);
      }
      return next;
    });
  };
  
  // Tournament detail view
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [detailView, setDetailView] = useState(false);
  const [detailTab, setDetailTab] = useState('registrations');
  
  // Data for selected tournament
  const [registrations, setRegistrations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [gameRsvps, setGameRsvps] = useState({});
  
  // Selection state
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [selectedAgeCategory, setSelectedAgeCategory] = useState('all');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  
  // Game filters
  const [gameTeamFilter, setGameTeamFilter] = useState('all');
  const [gameAgeFilter, setGameAgeFilter] = useState('all');

  // Playing XI selection
  const [showPlayingXIDialog, setShowPlayingXIDialog] = useState(false);
  const [playingXIGame, setPlayingXIGame] = useState(null);
  const [playingXISelected, setPlayingXISelected] = useState([]);
  const [savingPlayingXI, setSavingPlayingXI] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState('player_name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Add to team dialog
  const [showAddToTeamDialog, setShowAddToTeamDialog] = useState(false);
  const [playerToAdd, setPlayerToAdd] = useState(null);
  
  // Note dialog for when no team exists
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  
  // Team Chat state
  const [teamChatOpen, setTeamChatOpen] = useState(null);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const res = await api.get('/tournaments');
      setTournaments(res.data);
    } catch (error) {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const openTournamentDetail = async (tournament) => {
    setSelectedTournament(tournament);
    setDetailView(true);
    setDetailTab('registrations');
    setSelectedAgeCategory('all');
    setSelectedTeamId('');
    await fetchTournamentData(tournament.id);
  };

  const fetchTournamentData = useCallback(async (tournamentId) => {
    try {
      const [regsRes, teamsRes, gamesRes] = await Promise.all([
        api.get(`/tournaments/${tournamentId}/registrations`),
        api.get(`/tournaments/${tournamentId}/teams`),
        api.get(`/tournaments/${tournamentId}/games`)
      ]);
      
      setRegistrations(regsRes.data);
      setTeams(teamsRes.data);
      setGames(gamesRes.data);
      
      // Pre-select already selected players
      const alreadySelected = regsRes.data
        .filter(r => r.status === 'selected' || r.status === 'confirmed' || r.status === 'payment_pending')
        .map(r => r.player_id);
      setSelectedPlayers(alreadySelected);
      
      // Fetch RSVPs for all games
      const rsvpMap = {};
      for (const game of gamesRes.data) {
        try {
          const rsvpRes = await api.get(`/tournaments/${tournamentId}/games/${game.id}/rsvps`);
          rsvpMap[game.id] = rsvpRes.data;
        } catch (e) {
          rsvpMap[game.id] = [];
        }
      }
      setGameRsvps(rsvpMap);
    } catch (error) {
      toast.error('Failed to load tournament data');
    }
  }, []);

  const closeTournamentDetail = () => {
    setDetailView(false);
    setSelectedTournament(null);
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev => 
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handleSaveSelection = async () => {
    try {
      const currentlySelected = registrations
        .filter(r => r.status === 'selected' || r.status === 'confirmed' || r.status === 'payment_pending')
        .map(r => r.player_id);
      
      const newlySelected = selectedPlayers.filter(id => !currentlySelected.includes(id));
      const deselected = currentlySelected.filter(id => !selectedPlayers.includes(id));
      
      if (newlySelected.length > 0) {
        const result = await api.post(`/tournaments/${selectedTournament.id}/select-players`, newlySelected);
        const successCount = result.data.results.filter(r => r.status === 'selected').length;
        const emailSentCount = result.data.results.filter(r => r.payment_link_sent).length;
        toast.success(`${successCount} players selected, ${emailSentCount} payment links sent`);
      }
      
      for (const playerId of deselected) {
        try {
          await api.post(`/tournaments/${selectedTournament.id}/deselect-player/${playerId}`);
        } catch (error) {
          toast.error(`Could not deselect player: ${error.response?.data?.detail || 'Error'}`);
        }
      }
      
      if (deselected.length > 0) {
        toast.info(`${deselected.length} players deselected`);
      }
      
      await fetchTournamentData(selectedTournament.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save selection');
    }
  };

  const openAddToTeamDialog = (player) => {
    setPlayerToAdd(player);
    setShowAddToTeamDialog(true);
  };

  const handleAddToTeam = async () => {
    if (!playerToAdd || !selectedTeamId) {
      toast.error('Please select a team');
      return;
    }
    
    try {
      await api.post(`/tournaments/${selectedTournament.id}/teams/${selectedTeamId}/players`, {
        player_ids: [playerToAdd.player_id]
      });
      toast.success(`${playerToAdd.player_name} added to team`);
      setShowAddToTeamDialog(false);
      setPlayerToAdd(null);
      setSelectedTeamId('');
      await fetchTournamentData(selectedTournament.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player to team');
    }
  };

  const handleSendNoteToAdmin = async () => {
    if (!noteContent.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setNoteSubmitting(true);
    try {
      await api.post('/coach-notes', {
        note_type: 'general',
        date: getTodayInTz(businessTz),
        content: `Tournament Team Request - ${selectedTournament?.name}\n\nPlayer: ${playerToAdd?.player_name} (${playerToAdd?.age_category?.toUpperCase()})\n\nNote: ${noteContent}`,
        is_urgent: false
      });
      toast.success('Note sent to admin');
      setShowNoteDialog(false);
      setShowAddToTeamDialog(false);
      setNoteContent('');
      setPlayerToAdd(null);
    } catch (error) {
      toast.error('Failed to send note');
    } finally {
      setNoteSubmitting(false);
    }
  };

  // Filter registrations based on selected age category
  const filteredRegistrations = registrations.filter(r => 
    selectedAgeCategory === 'all' || r.age_category === selectedAgeCategory
  );

  // Get unique age categories
  const openPlayingXIDialog = async (game) => {
    setPlayingXIGame(game);
    setPlayingXISelected(game.selected_players || []);
    setShowPlayingXIDialog(true);
  };

  // Get all players available for a game (RSVPs + registered team members)
  const getGamePlayers = (game) => {
    const rsvps = gameRsvps[game?.id] || [];
    const yesRsvps = rsvps.filter(r => r.response === 'yes');
    const tentativeRsvps = rsvps.filter(r => r.response === 'tentative');
    const noRsvps = rsvps.filter(r => r.response === 'no');
    const rsvpPlayerIds = new Set(rsvps.map(r => r.player_id));

    // Get team members from registrations for this age category
    const teamRegs = registrations.filter(r =>
      r.age_category === game?.age_category &&
      (r.status === 'selected' || r.status === 'confirmed' || r.status === 'payment_pending') &&
      !rsvpPlayerIds.has(r.player_id)
    );

    return { yesRsvps, tentativeRsvps, noRsvps, teamRegs };
  };

  const togglePlayingXI = (playerId) => {
    setPlayingXISelected(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  const handleSavePlayingXI = async () => {
    if (!playingXIGame) return;
    setSavingPlayingXI(true);
    try {
      await api.post(`/tournaments/${selectedTournament.id}/games/${playingXIGame.id}/select-players`, {
        player_ids: playingXISelected
      });
      toast.success(`Selected ${playingXISelected.length} player(s) for the game`);
      setShowPlayingXIDialog(false);
      await fetchTournamentData(selectedTournament.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Playing XI');
    } finally {
      setSavingPlayingXI(false);
    }
  };

  const ageCategories = [...new Set(registrations.map(r => r.age_category))].sort();

  // Get teams for selected age category
  const _filteredTeams = teams.filter(t => 
    selectedAgeCategory === 'all' || t.age_category === selectedAgeCategory
  );

  // Check if player is on a team
  const getPlayerTeam = (playerId) => {
    return teams.find(t => t.player_ids?.includes(playerId));
  };

  const filteredTournaments = tournaments.filter(t => {
    if (activeTab === 'all') return true;
    return t.status === activeTab;
  });

  if (loading) {
    return (
      <DashboardLayout title="Tournaments">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Detail view for selected tournament
  if (detailView && selectedTournament) {
    return (
      <DashboardLayout title={`Tournament: ${selectedTournament.name}`}>
        <div className="space-y-6" data-testid="coach-tournament-detail">
          {/* Back button and header */}
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={closeTournamentDetail} data-testid="back-to-list">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tournaments
            </Button>
          </div>

          {/* Tournament info */}
          <Card>
            <CardContent className="p-4 lg:p-6">
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {selectedTournament.start_date} - {selectedTournament.end_date}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {selectedTournament.location}
                </span>
                <Badge className={statusColors[selectedTournament.status]}>
                  {selectedTournament.status}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={detailTab} onValueChange={setDetailTab}>
            <TabsList className="flex w-full max-w-lg overflow-x-auto">
              <TabsTrigger value="registrations" data-testid="tab-registrations" className="text-xs lg:text-sm whitespace-nowrap">
                <Users className="h-4 w-4 mr-1 shrink-0" />
                <span className="hidden sm:inline">Registrations</span>
                <span className="sm:hidden">Regs</span> ({registrations.length})
              </TabsTrigger>
              <TabsTrigger value="teams" data-testid="tab-teams" className="text-xs lg:text-sm whitespace-nowrap">
                <Shield className="h-4 w-4 mr-1 shrink-0" />
                Teams ({teams.length})
              </TabsTrigger>
              <TabsTrigger value="rsvps" data-testid="tab-rsvps" className="text-xs lg:text-sm whitespace-nowrap">
                <Clock className="h-4 w-4 mr-1 shrink-0" />
                <span className="hidden sm:inline">Game RSVPs</span>
                <span className="sm:hidden">RSVPs</span>
              </TabsTrigger>
            </TabsList>

            {/* Registrations Tab */}
            <TabsContent value="registrations" className="mt-6">
              <Card>
                <CardHeader className="border-b">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <CardTitle className="text-lg">Player Registrations</CardTitle>
                    <div className="flex items-center gap-2">
                      <Select value={selectedAgeCategory} onValueChange={setSelectedAgeCategory}>
                        <SelectTrigger className="w-40" data-testid="age-category-filter">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          {ageCategories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat.toUpperCase()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleSaveSelection} data-testid="save-selection-btn">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Save Selection
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {filteredRegistrations.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                      <p>No registrations found</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Select</TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'player_name') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('player_name');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            Player {sortField === 'player_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'age_category') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('age_category');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            Age Group {sortField === 'age_category' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'parent_name') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('parent_name');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            Parent {sortField === 'parent_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'team') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('team');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            Team {sortField === 'team' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'status') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('status');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRegistrations
                          .sort((a, b) => {
                            let aVal, bVal;
                            if (sortField === 'team') {
                              const aTeam = getPlayerTeam(a.player_id);
                              const bTeam = getPlayerTeam(b.player_id);
                              aVal = aTeam?.name || 'zzz'; // Sort unassigned to end
                              bVal = bTeam?.name || 'zzz';
                            } else {
                              aVal = a[sortField] || '';
                              bVal = b[sortField] || '';
                            }
                            if (sortDirection === 'asc') {
                              return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                            } else {
                              return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
                            }
                          })
                          .map(reg => {
                          const isLocked = reg.status === 'confirmed' || reg.payment_status === 'paid';
                          const playerTeam = getPlayerTeam(reg.player_id);
                          return (
                            <TableRow key={reg.id}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedPlayers.includes(reg.player_id)}
                                  onCheckedChange={() => togglePlayerSelection(reg.player_id)}
                                  disabled={isLocked}
                                  data-testid={`select-player-${reg.player_id}`}
                                />
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{reg.player_name}</p>
                                  {reg.notes && (
                                    <p className="text-xs text-slate-500">{reg.notes}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{reg.age_category?.toUpperCase()}</Badge>
                              </TableCell>
                              <TableCell>
                                <p className="text-sm">{reg.parent_name}</p>
                              </TableCell>
                              <TableCell>
                                {playerTeam ? (
                                  <Badge className="bg-blue-100 text-blue-700">{playerTeam.name}</Badge>
                                ) : (
                                  <span className="text-slate-400 text-sm">Not assigned</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge className={registrationStatusColors[reg.status]}>
                                  {reg.status.replace('_', ' ')}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {!playerTeam && (reg.status === 'selected' || reg.status === 'confirmed') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openAddToTeamDialog(reg)}
                                    data-testid={`add-to-team-${reg.player_id}`}
                                  >
                                    <UserPlus className="h-4 w-4 mr-1" />
                                    Add to Team
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Teams Tab */}
            <TabsContent value="teams" className="mt-6">
              <div className="space-y-4">
                {teams.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-slate-500">
                      <Shield className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                      <p className="font-medium">No teams created yet</p>
                      <p className="text-sm mt-1">Ask admin to create teams for this tournament</p>
                    </CardContent>
                  </Card>
                ) : (
                  teams.map(team => (
                    <Card key={team.id} data-testid={`team-card-${team.id}`}>
                      <CardHeader className="border-b py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Shield className="h-5 w-5 text-blue-500" />
                            <div>
                              <CardTitle className="text-lg">{team.name}</CardTitle>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline">{team.age_category?.toUpperCase()}</Badge>
                                {team.division && (
                                  <Badge variant="secondary">{team.division}</Badge>
                                )}
                                {team.coach_name && (
                                  <span className="text-sm text-slate-500">Coach: {team.coach_name}</span>
                                )}
                                {team.cricclubs_url && (
                                  <a
                                    href={team.cricclubs_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                                    data-testid={`cricclubs-link-${team.id}`}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    CricClubs Schedule
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setTeamChatOpen(team.id)}
                              className="text-blue-600 hover:text-blue-700"
                              data-testid={`chat-team-${team.id}`}
                            >
                              <MessageCircle className="h-4 w-4 mr-1" />
                              Chat
                            </Button>
                            <Badge className="bg-slate-100 text-slate-700">
                              {team.player_count || 0} players
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4">
                        {team.players && team.players.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {team.players.map(player => (
                              <div key={player.player_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                                <UserCheck className="h-4 w-4 text-green-500" />
                                <span className="text-sm">{player.player_name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-sm">No players assigned to this team</p>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* RSVPs Tab */}
            <TabsContent value="rsvps" className="mt-6">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white rounded-lg border">
                <Filter className="h-4 w-4 text-slate-400" />
                
                {/* Team Filter */}
                {(() => {
                  const gameTeams = [...new Set(games.map(g => g.team_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
                  if (gameTeams.length > 0) {
                    return (
                      <Select value={gameTeamFilter} onValueChange={setGameTeamFilter}>
                        <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="rsvp-team-filter">
                          <SelectValue placeholder="All Teams" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Teams</SelectItem>
                          {gameTeams.map(team => (
                            <SelectItem key={team} value={team}>{team}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  }
                  return null;
                })()}
                
                {/* Age Category Filter */}
                {(() => {
                  const ages = [...new Set(games.map(g => g.age_category).filter(Boolean))].sort((a, b) => a.localeCompare(b));
                  if (ages.length > 1) {
                    return (
                      <Select value={gameAgeFilter} onValueChange={setGameAgeFilter}>
                        <SelectTrigger className="w-[120px] h-8 text-sm" data-testid="rsvp-age-filter">
                          <SelectValue placeholder="All Ages" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Ages</SelectItem>
                          {ages.map(age => (
                            <SelectItem key={age} value={age}>{age.toUpperCase()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  }
                  return null;
                })()}
                
                {/* Clear Filters */}
                {(gameTeamFilter !== 'all' || gameAgeFilter !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 text-xs"
                    onClick={() => {
                      setGameTeamFilter('all');
                      setGameAgeFilter('all');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
              
              <div className="space-y-4">
                {games.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-slate-500">
                      <Clock className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                      <p className="font-medium">No games scheduled yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  games
                    .filter(game => {
                      if (gameTeamFilter !== 'all' && game.team_name !== gameTeamFilter) return false;
                      if (gameAgeFilter !== 'all' && game.age_category !== gameAgeFilter) return false;
                      // Filter out completed/archived games for main schedule
                      if (game.status === 'completed' || game.is_archived) return false;
                      return true;
                    })
                    .sort((a, b) => new Date(a.game_date) - new Date(b.game_date))
                    .map(game => {
                    const rsvps = gameRsvps[game.id] || [];
                    const yesCnt = rsvps.filter(r => r.response === 'yes').length;
                    const noCnt = rsvps.filter(r => r.response === 'no').length;
                    const tentCnt = rsvps.filter(r => r.response === 'tentative').length;
                    
                    return (
                      <Card key={game.id} data-testid={`game-rsvp-${game.id}`}>
                        <CardHeader className="border-b py-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                {game.team_name || 'TBD'} vs {game.opponent_team}
                                <Badge variant="outline">{game.age_category?.toUpperCase()}</Badge>
                                {game.division && (
                                  <Badge variant="secondary">{game.division}</Badge>
                                )}
                              </CardTitle>
                              <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  {game.game_date}
                                </span>
                                <span>{game.start_time}</span>
                                {game.location && (
                                  <LocationLink location={game.location} locationUrl={game.location_url} />
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Badge className="bg-green-100 text-green-700">Yes: {yesCnt}</Badge>
                              <Badge className="bg-red-100 text-red-700">No: {noCnt}</Badge>
                              <Badge className="bg-yellow-100 text-yellow-700">Maybe: {tentCnt}</Badge>
                              {game.selected_players?.length > 0 && (
                                <Badge className="bg-blue-100 text-blue-700">XI: {game.selected_players.length}</Badge>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-slate-600">RSVPs</p>
                            <Button
                              size="sm"
                              variant={game.selected_players?.length > 0 ? "secondary" : "outline"}
                              onClick={() => openPlayingXIDialog(game)}
                              className="gap-1"
                              data-testid={`select-xi-${game.id}`}
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                              {game.selected_players?.length > 0 ? `Selected (${game.selected_players.length})` : 'Select Players'}
                            </Button>
                          </div>
                          {rsvps.length === 0 ? (
                            <p className="text-slate-500 text-sm">No RSVPs received yet</p>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {rsvps.map(rsvp => (
                                <div 
                                  key={rsvp.id} 
                                  className={`flex items-center justify-between p-2 rounded ${
                                    rsvp.response === 'yes' ? 'bg-green-50' :
                                    rsvp.response === 'no' ? 'bg-red-50' : 'bg-yellow-50'
                                  }`}
                                >
                                  <span className="text-sm font-medium">{rsvp.player_name}</span>
                                  <Badge className={rsvpColors[rsvp.response]}>
                                    {rsvp.response}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
                
                {/* Completed Games Section */}
                {(() => {
                  const completedGames = games.filter(g => g.status === 'completed' || g.is_archived);
                  if (completedGames.length === 0) return null;
                  
                  return (
                    <Card className="mt-6">
                      <CardHeader className="py-4 border-b bg-slate-50">
                        <div className="flex items-center gap-2">
                          <Archive className="h-5 w-5 text-slate-500" />
                          <h3 className="font-semibold text-slate-700">Completed Games ({completedGames.length})</h3>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          {completedGames
                            .sort((a, b) => new Date(b.game_date) - new Date(a.game_date))
                            .map(game => {
                              const rsvps = gameRsvps[game.id] || [];
                              const yesCnt = rsvps.filter(r => r.response === 'yes').length;
                              
                              return (
                                <div 
                                  key={game.id} 
                                  className="bg-slate-100 rounded-lg border border-slate-200 p-3 opacity-75"
                                >
                                  <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-3">
                                      <div className="p-1.5 bg-slate-200 rounded">
                                        <CheckCircle2 className="h-4 w-4 text-slate-500" />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-medium text-sm text-slate-700">
                                            {game.team_name || 'TBD'} vs {game.opponent_team || 'TBD'}
                                          </span>
                                          <Badge variant="outline" className="uppercase text-xs">
                                            {game.age_category}
                                          </Badge>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                                          <span>{game.game_date}</span>
                                          <span>{game.start_time}</span>
                                          {game.location && (
                                            <LocationLink location={game.location} locationUrl={game.location_url} />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge className="bg-green-100 text-green-700 text-xs">{yesCnt} attended</Badge>
                                      <Badge className="bg-slate-200 text-slate-600 text-xs">Completed</Badge>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>
            </TabsContent>
          </Tabs>

          {/* Add to Team Dialog */}
          <Dialog open={showAddToTeamDialog} onOpenChange={setShowAddToTeamDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Player to Team</DialogTitle>
                <DialogDescription>
                  Select a team to add {playerToAdd?.player_name} to.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                  <SelectTrigger data-testid="select-team-dropdown">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams
                      .filter(t => t.age_category === playerToAdd?.age_category)
                      .map(team => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name} ({team.player_count || 0} players)
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {teams.filter(t => t.age_category === playerToAdd?.age_category).length === 0 && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 mb-3">
                      No teams available for {playerToAdd?.age_category?.toUpperCase()} age group.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNoteDialog(true)}
                      className="w-full"
                      data-testid="send-note-to-admin"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Send Note to Admin
                    </Button>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddToTeamDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddToTeam} 
                  disabled={!selectedTeamId}
                  data-testid="confirm-add-to-team"
                >
                  Add to Team
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Note to Admin Dialog */}
          <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-500" />
                  Request Team Creation
                </DialogTitle>
                <DialogDescription>
                  Send a note to admin requesting a team for this player.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Tournament: <span className="font-medium">{selectedTournament?.name}</span></p>
                  <p className="text-sm text-slate-600 mt-1">Player: <span className="font-medium">{playerToAdd?.player_name}</span></p>
                  <p className="text-sm text-slate-600 mt-1">Age Group: <span className="font-medium">{playerToAdd?.age_category?.toUpperCase()}</span></p>
                </div>
                <div className="space-y-2">
                  <Label>Your Note to Admin</Label>
                  <Textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="E.g., Please create a team for U10 players. We have enough registrations now..."
                    rows={3}
                    data-testid="admin-note-content"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSendNoteToAdmin}
                  disabled={noteSubmitting || !noteContent.trim()}
                  data-testid="submit-admin-note"
                >
                  {noteSubmitting ? 'Sending...' : 'Send Note'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Team Chat Dialog */}
          <Dialog open={!!teamChatOpen} onOpenChange={(open) => !open && setTeamChatOpen(null)}>
            <DialogContent className="max-w-4xl h-[85vh] p-0">
              {teamChatOpen && (
                <TeamChat 
                  teamId={teamChatOpen}
                  teamName={teams.find(t => t.id === teamChatOpen)?.name || 'Team Chat'}
                  onBack={() => setTeamChatOpen(null)}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* Playing XI Selection Dialog */}
          <Dialog open={showPlayingXIDialog} onOpenChange={setShowPlayingXIDialog}>
            <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-blue-500" />
                  Select Players
                </DialogTitle>
                <DialogDescription>
                  {playingXIGame && `${playingXIGame.team_name || 'TBD'} vs ${playingXIGame.opponent_team} — ${playingXIGame.game_date}`}
                </DialogDescription>
              </DialogHeader>
              
              {playingXIGame && (() => {
                const { yesRsvps, tentativeRsvps, teamRegs } = getGamePlayers(playingXIGame);
                const allPlayers = [
                  ...yesRsvps.map(r => ({ ...r, source: 'yes' })),
                  ...tentativeRsvps.map(r => ({ ...r, source: 'tentative' })),
                  ...teamRegs.map(r => ({ player_id: r.player_id, player_name: r.player_name, source: 'registered' })),
                ];
                
                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">
                        Selected: <span className="font-bold text-blue-600">{playingXISelected.length}</span>
                      </span>
                      <div className="flex gap-2">
                        {yesRsvps.length > 0 && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setPlayingXISelected(yesRsvps.map(r => r.player_id))}>
                            Select All Yes
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                          setPlayingXISelected(allPlayers.map(p => p.player_id));
                        }}>
                          Select All
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setPlayingXISelected([])}>
                          Clear
                        </Button>
                      </div>
                    </div>

                    {yesRsvps.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Yes RSVPs ({yesRsvps.length})</p>
                        <div className="space-y-1">
                          {yesRsvps.map(rsvp => (
                            <div key={rsvp.player_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                              playingXISelected.includes(rsvp.player_id) ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'
                            }`} onClick={() => togglePlayingXI(rsvp.player_id)}>
                              <Checkbox
                                checked={playingXISelected.includes(rsvp.player_id)}
                                onCheckedChange={() => togglePlayingXI(rsvp.player_id)}
                              />
                              <span className="text-sm">{rsvp.player_name}</span>
                              <Badge className="ml-auto bg-green-100 text-green-700 text-xs">Yes</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {tentativeRsvps.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-amber-700 mb-1">Maybe RSVPs ({tentativeRsvps.length})</p>
                        <div className="space-y-1">
                          {tentativeRsvps.map(rsvp => (
                            <div key={rsvp.player_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                              playingXISelected.includes(rsvp.player_id) ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'
                            }`} onClick={() => togglePlayingXI(rsvp.player_id)}>
                              <Checkbox
                                checked={playingXISelected.includes(rsvp.player_id)}
                                onCheckedChange={() => togglePlayingXI(rsvp.player_id)}
                              />
                              <span className="text-sm">{rsvp.player_name}</span>
                              <Badge className="ml-auto bg-amber-100 text-amber-700 text-xs">Maybe</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {teamRegs.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-700 mb-1">Team Registered ({teamRegs.length})</p>
                        <div className="space-y-1">
                          {teamRegs.map(reg => (
                            <div key={reg.player_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                              playingXISelected.includes(reg.player_id) ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'
                            }`} onClick={() => togglePlayingXI(reg.player_id)}>
                              <Checkbox
                                checked={playingXISelected.includes(reg.player_id)}
                                onCheckedChange={() => togglePlayingXI(reg.player_id)}
                              />
                              <span className="text-sm">{reg.player_name}</span>
                              <Badge className="ml-auto bg-slate-100 text-slate-600 text-xs">Registered</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {yesRsvps.length === 0 && tentativeRsvps.length === 0 && teamRegs.length === 0 && (
                      <div className="text-center py-6 text-slate-400">
                        <Users className="h-8 w-8 mx-auto mb-2" />
                        <p className="text-sm">No players available for selection</p>
                      </div>
                    )}
                  </div>
                );
              })()}
              
              <DialogFooter className="mt-2">
                <Button variant="outline" onClick={() => setShowPlayingXIDialog(false)}>Cancel</Button>
                <Button
                  onClick={handleSavePlayingXI}
                  disabled={savingPlayingXI || playingXISelected.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-playing-xi-btn"
                >
                  {savingPlayingXI ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                  Save ({playingXISelected.length} selected)
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </div>
      </DashboardLayout>
    );
  }

  // Tournament list view
  return (
    <DashboardLayout title="Tournament Team Selection">
      <div className="space-y-6" data-testid="coach-tournaments">
        <p className="text-slate-600">Select players for tournament teams and view RSVPs</p>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full overflow-x-auto">
            <TabsTrigger value="active" className="text-xs lg:text-sm whitespace-nowrap">Active</TabsTrigger>
            <TabsTrigger value="future" className="text-xs lg:text-sm whitespace-nowrap">Upcoming</TabsTrigger>
            <TabsTrigger value="past" className="text-xs lg:text-sm whitespace-nowrap">Past</TabsTrigger>
            <TabsTrigger value="all" className="text-xs lg:text-sm whitespace-nowrap">All</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {filteredTournaments.length === 0 ? (
              <Card>
                <CardContent className="p-8 lg:p-12 text-center">
                  <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No tournaments found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {filteredTournaments.map(tournament => {
                  const isExpanded = expandedTournamentCards.has(tournament.id);
                  return (
                  <Card key={tournament.id} className="hover:border-blue-200 transition-colors">
                    <CardContent className="p-4">
                      {/* Collapsed Header - Always Visible */}
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleTournamentCard(tournament.id)}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <ChevronRight className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-heading font-bold truncate">{tournament.name}</h3>
                              <Badge className={`${statusColors[tournament.status]} text-xs`}>
                                {tournament.status}
                              </Badge>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {tournament.start_date} - {tournament.end_date} • {tournament.age_categories?.map(c => c.toUpperCase()).join(', ')}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openTournamentDetail(tournament);
                          }}
                          data-testid={`manage-tournament-${tournament.id}`}
                        >
                          Manage
                        </Button>
                      </div>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="mt-3 pl-7 space-y-2">
                          <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {tournament.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-4 w-4" />
                              {cs()}{tournament.fee_amount}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            {tournament.age_categories?.map(cat => (
                              <Badge key={cat} variant="outline">{cat.toUpperCase()}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

    </DashboardLayout>
  );
};

export default CoachTournaments;
